import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart2, Users, Package, TrendingUp, Download, Calendar,
  DollarSign, PieChart
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from 'recharts';

const ReportsTab = () => {
  const [period, setPeriod] = useState('month');

  const topCustomers = [
    { name: 'Al Rajhi Trading', revenue: 1250000, orders: 48 },
    { name: 'Metro Retail', revenue: 980000, orders: 62 },
    { name: 'Saudi Foods Co', revenue: 760000, orders: 35 },
    { name: 'Gulf Distributors', revenue: 520000, orders: 28 },
    { name: 'Desert Goods LLC', revenue: 310000, orders: 19 },
  ];

  const topProducts = [
    { name: 'Premium Olive Oil 5L', sku: 'SKU-8821', units: 12500, revenue: 625000 },
    { name: 'Rice Basmati 25kg', sku: 'SKU-5500', units: 8200, revenue: 410000 },
    { name: 'Tomato Paste 1kg', sku: 'SKU-7701', units: 15000, revenue: 375000 },
    { name: 'Olive Oil Blend 5L', sku: 'SKU-8822', units: 6000, revenue: 300000 },
  ];

  const trendData = [
    { month: 'Sep', revenue: 3.2 },
    { month: 'Oct', revenue: 3.5 },
    { month: 'Nov', revenue: 3.8 },
    { month: 'Dec', revenue: 4.0 },
    { month: 'Jan', revenue: 3.9 },
    { month: 'Feb', revenue: 4.1 },
    { month: 'Mar', revenue: 4.2 },
  ];

  const categoryData = [
    { name: 'Oils', value: 35, color: '#0d9488' },
    { name: 'Grains', value: 28, color: '#2563eb' },
    { name: 'Sauces & Paste', value: 22, color: '#f59e0b' },
    { name: 'Other', value: 15, color: '#6b7280' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-900">Reports & Analytics</h2>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="quarter">This quarter</option>
            <option value="year">This year</option>
          </select>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Sales trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Sales trend (SAR M)</h3>
        </div>
        <div className="p-6 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `${v}`} />
              <Tooltip formatter={(v) => [`SAR ${v}M`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#0d9488" radius={[4, 4, 0, 0]} name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top customers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top customers</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {topCustomers.map((c, i) => (
              <div key={c.name} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">{i + 1}</span>
                  <div>
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.orders} orders</p>
                  </div>
                </div>
                <p className="font-semibold text-gray-900">SAR {(c.revenue / 1000).toFixed(0)}K</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top products */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">Most popular products</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {topProducts.map((p) => (
              <div key={p.sku} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.sku} · {p.units.toLocaleString()} units</p>
                </div>
                <p className="font-semibold text-gray-900">SAR {(p.revenue / 1000).toFixed(0)}K</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category mix */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Revenue by category</h3>
        </div>
        <div className="p-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPie>
              <Pie
                data={categoryData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, value }) => `${name} ${value}%`}
              >
                {categoryData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [`${v}%`, 'Share']} />
              <Legend />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ReportsTab;
